import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';

import { type AudienceOptionType } from '@/api/audience';
import {
  type NotificationInputType,
  type NotificationPageMeta,
  type RecurringNotificationSubscriptionType,
  type NotificationSubscriptionRecurringDataType,
  mutateNotificationCreate,
  mutateNotificationUpdate,
} from '@/api/settings';
import { type UserOptionType } from '@/api/user';
import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import {
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/Form/pickers/primitives/select';
import { type SelectFieldOption } from '@/components/Form/tanstack/field-components';
import { usePageContext } from '@/hooks/page/PageContext';

import NotificationSubscriptionCommonFields from './NotificationSubscriptionCommonFields';

type Props = {
  subscription?: NotificationSubscriptionRecurringDataType | null;
  audience?: AudienceOptionType;
  notificationPageMeta?: NotificationPageMeta;
  notificationType?: RecurringNotificationSubscriptionType;
  onCancel: () => void;
  onSaveComplete: (success: boolean) => void;
};

type RecurringValues = {
  name: string;
  recipients: UserOptionType[];
  repeats: SelectFieldOption;
  reportingWindow: SelectFieldOption;
  deliveryDays: string[];
};

const RecurringNotificationSubscriptionDialog = ({
  subscription,
  audience,
  notificationPageMeta,
  notificationType = 'recurring',
  onCancel,
  onSaveComplete,
}: Props) => {
  const { title } = usePageContext();
  const isEditing = subscription?.id != null;
  const currentNotificationType = subscription?.type ?? notificationType;
  const isAIDigest = currentNotificationType === 'ai_digest';

  const reportingWindowOptions = useMemo<{ value: string; label: string }[]>(
    () => [
      { value: '7', label: t`Last 7 Days` },
      { value: '14', label: t`Last 14 Days` },
      { value: '30', label: t`Last 30 Days` },
      { value: '60', label: t`Last 60 Days` },
      { value: '90', label: t`Last 90 Days` },
      { value: '180', label: t`Last 180 Days` },
    ],
    [],
  );

  const deliveryFrequencyOptions = useMemo(
    () => [
      { value: '1', label: t`Every Week` },
      { value: '2', label: t`Every 2 Weeks` },
      { value: '3', label: t`Every 3 Weeks` },
      { value: '4', label: t`Every 4 Weeks` },
      { value: '5', label: t`Every 5 Weeks` },
      { value: '6', label: t`Every 6 Weeks` },
      { value: '7', label: t`Every 7 Weeks` },
      { value: '8', label: t`Every 8 Weeks` },
    ],
    [],
  );

  const selectOptionSchema = z.object({
    label: z.string(),
    value: z.string(),
  });

  const formSchema = z.object({
    name: z.string().min(1, t`Name is required`),
    recipients: z.custom<UserOptionType[]>((value) => Array.isArray(value)),
    repeats: selectOptionSchema
      .transform((value) => Number(value.value))
      .refine((value) => Number.isFinite(value) && value > 0, t`Repeats is Required`),
    reportingWindow: selectOptionSchema
      .transform((value) => Number(value.value))
      .refine((value) => Number.isFinite(value) && value > 0, t`Reporting Window is Required`),
    deliveryDays: z
      .array(z.string())
      .min(1, t`Delivery Day is Required. Please choose at least one.`),
  });

  const selectedRepeats =
    deliveryFrequencyOptions.find(
      (option) => option.value === `${subscription?.repeatsWeeks ?? '1'}`,
    ) ?? deliveryFrequencyOptions[0];
  const selectedReportingWindow =
    reportingWindowOptions.find(
      (option) => option.value === `${subscription?.reportingWindowDays ?? '30'}`,
    ) ?? reportingWindowOptions[0];

  const form = useAppForm({
    defaultValues: {
      name: subscription?.name ?? '',
      recipients: subscription?.additionalRecipients ?? [],
      repeats: selectedRepeats,
      reportingWindow: selectedReportingWindow,
      deliveryDays: subscription?.deliveryDays ?? [`${dayjs().day()}`],
    } satisfies RecurringValues,
    validators: {
      onSubmit: formSchema,
      onSubmitAsync: async ({ value }) => {
        const parsed = formSchema.parse(value);
        let success = false;
        const notificationInput: Omit<NotificationInputType, 'urlTemplate'> = {
          name: parsed.name,
          additionalRecipients: parsed.recipients.map((recipient) => recipient.id),
          repeatsWeeks: parsed.repeats,
          reportingWindowDays: parsed.reportingWindow,
          sunday: parsed.deliveryDays.includes('0'),
          monday: parsed.deliveryDays.includes('1'),
          tuesday: parsed.deliveryDays.includes('2'),
          wednesday: parsed.deliveryDays.includes('3'),
          thursday: parsed.deliveryDays.includes('4'),
          friday: parsed.deliveryDays.includes('5'),
          saturday: parsed.deliveryDays.includes('6'),
        };

        try {
          if (isEditing && subscription) {
            const response = await mutateNotificationUpdate({
              ...notificationInput,
              id: subscription.id,
              urlTemplate: subscription.urlTemplate,
            });

            success = !!response;
          } else {
            const urlPath = window.location.pathname + window.location.search;
            const urlTemplate = `${urlPath}${urlPath.indexOf('?') === -1 ? '?' : '&'}startDate={startDate}&endDate={endDate}`;

            const response = await mutateNotificationCreate({
              ...notificationInput,
              ...(isAIDigest ? { type: 'ai_digest' as const } : {}),
              urlTemplate,
            });

            success = !!response;
          }
        } catch {
          success = false;
        }

        onSaveComplete(success);
      },
    },
  });

  const reportingWindowLabel = useStore(form.store, (state) => state.values.reportingWindow.label);
  const isSaving = useStore(form.store, (state) => state.isSubmitting);
  const reportName = useStore(form.store, (state) => state.values.name);
  const isNameTouched = useStore(form.store, (state) => state.fieldMeta.name?.isTouched ?? false);
  const defaultReportName = (() => {
    const reportNameParts: string[] = [];
    if (title) {
      reportNameParts.push(title);
    }
    if (!notificationPageMeta?.hasNoAudienceFilter) {
      if (audience?.name) {
        reportNameParts.push(audience.name);
      } else {
        reportNameParts.push(t`TAM`);
      }
    }
    if (reportingWindowLabel) {
      reportNameParts.push(reportingWindowLabel);
    }
    return reportNameParts.join(' - ');
  })();

  useEffect(() => {
    if (
      isAIDigest ||
      isEditing ||
      isNameTouched ||
      !defaultReportName ||
      reportName === defaultReportName
    ) {
      return;
    }

    form.setFieldValue('name', defaultReportName);
  }, [defaultReportName, form, isAIDigest, isEditing, isNameTouched, reportName]);

  const handleCancel = () => {
    form.reset();
    onCancel();
  };

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (isSaving) return;
        if (!isOpen) handleCancel();
      }}
    >
      <DialogContent showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>
            {isAIDigest ? (
              isEditing ? (
                <Trans>Edit AI Digest</Trans>
              ) : (
                <Trans>Schedule AI Digest</Trans>
              )
            ) : isEditing ? (
              <Trans>Edit Notification</Trans>
            ) : (
              <Trans>Schedule Email</Trans>
            )}
          </DialogTitle>
          {isAIDigest ? (
            <DialogDescription>
              <Trans>
                AI Digest is a personalized marketing summary email sent to all recipients that
                highlights your most important wins, risks, and next best actions, with direct links
                to take action quickly.
              </Trans>
            </DialogDescription>
          ) : (
            <DialogDescription>
              <Trans>
                Schedule an email to be sent to all recipients at a regular interval with metrics
                from this page so you can stay on top of your marketing performance.
              </Trans>
            </DialogDescription>
          )}
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            form.handleSubmit();
          }}
        >
          <div className="grid grid-cols-12 gap-4">
            <NotificationSubscriptionCommonFields
              form={form}
              fields={{
                name: 'name',
                recipients: 'recipients',
              }}
              nameLabel={isAIDigest ? <Trans>Scheduled Digest Name</Trans> : undefined}
              nameInputId="recurring-notification-name"
              recipientsInputId="recurring-notification-recipients"
              isSubmitting={isSaving}
            />
            <form.AppField name="repeats">
              {(field) => (
                <field.Field className="col-span-6 sm:col-span-5 md:col-span-4">
                  <FieldLabel htmlFor="recurring-notification-repeats">
                    <Trans>Repeats</Trans>
                  </FieldLabel>
                  <field.Select items={deliveryFrequencyOptions}>
                    <field.SelectTrigger id="recurring-notification-repeats" className="w-full">
                      <SelectValue placeholder={t`Choose Interval`} />
                    </field.SelectTrigger>
                    <SelectContent>
                      {deliveryFrequencyOptions.map((item) => (
                        <SelectItem key={item.value} value={item}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </field.Select>
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
            <form.AppField name="reportingWindow">
              {(field) => (
                <field.Field className="col-span-6 sm:col-span-5 md:col-span-4">
                  <FieldLabel htmlFor="recurring-notification-reporting-window">
                    <Trans>Reporting Window</Trans>
                  </FieldLabel>
                  <field.Select items={reportingWindowOptions}>
                    <field.SelectTrigger
                      id="recurring-notification-reporting-window"
                      className="w-full"
                    >
                      <SelectValue placeholder={t`Choose Reporting Window`} />
                    </field.SelectTrigger>
                    <SelectContent>
                      {reportingWindowOptions.map((item) => (
                        <SelectItem key={item.value} value={item}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </field.Select>
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
            <form.AppField name="deliveryDays">
              {(field) => (
                <field.Field className="col-span-full">
                  <FieldLabel htmlFor="recurring-notification-delivery-days">
                    <Trans>Delivery Day</Trans>
                  </FieldLabel>
                  <field.DayChooser
                    id="recurring-notification-delivery-days"
                    selectionMode="multiple"
                  />
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
          </div>
        </form>
        <DialogFooter>
          <DialogClose
            render={
              <Button size="large" disabled={isSaving}>
                <Trans>Cancel</Trans>
              </Button>
            }
          />
          <form.AppForm>
            <form.SubmitButton size="large" variant="on-primary">
              <Trans>Save</Trans>
            </form.SubmitButton>
          </form.AppForm>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecurringNotificationSubscriptionDialog;
